import {ExternalLinkIcon} from '@heroicons/react/outline';
import React, {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';
import {requestableBusinessKYBDocuments} from '@afriex/core/utils';

import {Badge, Button, Link, PermissionsProvider} from '@/components';
import {useCountryList} from '@/context/CountryListContext';
import {InfoRow} from '@/screens/Merchants/Kyc/common';
import {getVerificationBadge} from '@/screens/Merchants/Kyc/verificationBadge';
import {statusColors} from '@/types/Transaction';
import {mapCountryCodeToFlags} from '@/utils/countries';
import {capitalizeWords} from '@/utils/dashboard';
import {toSafeExternalUrl} from '@/utils/externalUrl';
import {formatBusinessAddress} from '@/utils/merchant';

import RequestAdditionalDocumentsModal from '../RequestAdditionalDocumentsModal';
import KYBModal from './KYBModal';
import PostFundingConfigForm from './PostFundingConfigForm';
import type {KYBSummaryProps} from './types';

const Summary: React.FC<KYBSummaryProps> = ({
  business,
  onSubmit,
  onRequestAdditionalDocuments,
  isKycSubmitting,
  extraRow,
  onFlagUpdate,
  businessLoadingFlags,
  onPostFundingConfigUpdate,
  isPostFundingConfigSubmitting,
}) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const country = business?.countryCode?.toUpperCase() || '';
  const {
    isVerified = false,
    isApprovedForAPI = false,
    isApprovedForMarketPlace = false,
    isDeactivated = false,
    isApprovedForFeeWallet = false,
    isApprovedForPostFunding = false,
  } = business?.meta?.flags ?? {};

  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [openRequestDocumentsModal, setOpenRequestDocumentsModal] =
    useState(false);
  const [modalType, setModalType] = useState<'Profile' | 'Documents'>(
    'Profile',
  );
  const businessWebsite = business?.meta?.kyb?.data?.BUSINESS_WEBSITE;
  // Merchant-supplied, so it is only linked when it is a plain http(s) URL.
  const businessWebsiteUrl = toSafeExternalUrl(businessWebsite);
  const fullBusinessAddress = formatBusinessAddress(
    business?.meta?.kyb?.data?.BUSINESS_ADDRESS,
  );
  return (
    <div className="mt-5">
      <div className="flex flex-col md:flex-row bg-white dark:bg-transparent rounded-lg w-full px-5 py-5 gap-5 border border-gray-200 dark:border-dark-borderMid">
        <div className="bg-white dark:bg-transparent rounded-lg w-full">
          <div className="flex flex-col md:flex-row gap-3 justify-between mb-3">
            <div className="flex md:items-center gap-4">
              <h2 className="font-semibold text-md md:text-lg self-center">
                {business.name}
              </h2>
              {getVerificationBadge(isVerified || false)}
            </div>

            <div className="flex md:justify-end gap-3 my-3 md:my-0 md:items-center">
              <PermissionsProvider
                permission={AfriexAdminPermission.BusinessKYCUpdate}
              >
                <Button
                  variant="outline"
                  onClick={() => {
                    setModalType('Profile');
                    setOpenUploadModal(!openUploadModal);
                  }}
                >
                  Update Info
                </Button>

                <Button
                  variant="solid"
                  onClick={() => {
                    setModalType('Documents');
                    setOpenUploadModal(!openUploadModal);
                  }}
                >
                  Upload Documents
                </Button>
              </PermissionsProvider>

              <PermissionsProvider
                permission={AfriexAdminPermission.BusinessDocumentRequestCreate}
              >
                <Button
                  variant="outline"
                  onClick={() => setOpenRequestDocumentsModal(true)}
                >
                  Request Additional Documents
                </Button>
              </PermissionsProvider>
            </div>
          </div>
          <div>
            {/* Country */}
            <InfoRow
              label="Country"
              value={
                country + ' ' + mapCountryCodeToFlags(countryList)[country]
              }
            />
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 text-sm  dark:text-slate-100">
                Business Details
              </p>
              <p className="text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
                <Link
                  to={`/merchants/businesses/${business?.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="ghost"
                    className="no-padding no-margin hover:underline"
                    rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
                  >
                    View Business Details
                  </Button>
                </Link>
              </p>
            </div>
            {/* Business website */}
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 text-sm  dark:text-slate-100">
                Business Website
              </p>
              <p className="text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
                {businessWebsiteUrl ? (
                  <Link
                    to={businessWebsiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      variant="ghost"
                      className="no-padding no-margin hover:underline"
                      rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
                    >
                      {businessWebsite}
                    </Button>
                  </Link>
                ) : (
                  businessWebsite || '-'
                )}
              </p>
            </div>
            {/* Business Number */}
            <InfoRow
              label="Business Number"
              value={business?.meta?.kyb?.data?.BUSINESS_NUMBER}
            />
            {/* Business Nature */}
            <InfoRow
              label="Business Nature"
              value={business?.meta?.kyb?.data?.BUSINESS_NATURE}
            />
            {/* Business Type */}
            <InfoRow
              label="Business Type"
              value={capitalizeWords(
                (
                  business?.meta?.businessType ||
                  business?.meta?.kyb?.data?.BUSINESS_TYPE
                )?.replace(/_/g, ' ') || '',
              )}
            />
            {/* Business Address */}
            <InfoRow label="Business Address" value={fullBusinessAddress} />
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900 rounded">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Verified
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 break-words flex gap-3">
                <Badge
                  dataCy="merchant-kyb-verified-badge"
                  label={isVerified ? 'Yes' : 'No'}
                  colorScheme={
                    statusColors[isVerified ? 'successful' : 'failed']
                  }
                  className="px-4 !rounded-3xl"
                />
                <PermissionsProvider
                  permission={AfriexAdminPermission.BusinessFlagUpdate}
                >
                  <Button
                    data-cy="merchant-kyb-verified-toggle"
                    disabled={businessLoadingFlags['isVerified']}
                    variant="unStyled"
                    onClick={() =>
                      onFlagUpdate({
                        isVerified: !isVerified,
                      })
                    }
                    className="hover:underline text-cyan-700"
                  >
                    {isVerified ? 'Unverify' : 'Verify'}
                  </Button>
                </PermissionsProvider>
              </p>
            </div>
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900 rounded">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Approved for API
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 break-words flex gap-3">
                <Badge
                  dataCy="merchant-kyb-api-approval-badge"
                  label={isApprovedForAPI ? 'Yes' : 'No'}
                  colorScheme={
                    statusColors[isApprovedForAPI ? 'successful' : 'failed']
                  }
                  className="px-4 !rounded-3xl"
                />
                <Button
                  data-cy="merchant-kyb-api-approval-toggle"
                  disabled={businessLoadingFlags['isApprovedForAPI']}
                  variant="unStyled"
                  onClick={() =>
                    onFlagUpdate({
                      isApprovedForAPI: !isApprovedForAPI,
                    })
                  }
                  className="hover:underline text-cyan-700"
                >
                  {!isApprovedForAPI ? 'Approve' : 'Unapprove'}
                </Button>
              </p>
            </div>
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900 rounded">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Approved for Marketplace
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 break-words flex gap-3">
                <Badge
                  dataCy="merchant-kyb-marketplace-approval-badge"
                  label={isApprovedForMarketPlace ? 'Yes' : 'No'}
                  colorScheme={
                    statusColors[
                      isApprovedForMarketPlace ? 'successful' : 'failed'
                    ]
                  }
                  className="px-4 !rounded-3xl"
                />
                <Button
                  data-cy="merchant-kyb-marketplace-approval-toggle"
                  disabled={
                    (businessLoadingFlags as Record<string, boolean>)[
                      'isApprovedForMarketPlace'
                    ]
                  }
                  variant="unStyled"
                  onClick={() =>
                    onFlagUpdate({
                      isApprovedForMarketPlace: !isApprovedForMarketPlace,
                    })
                  }
                  className="hover:underline text-cyan-700"
                >
                  {!isApprovedForMarketPlace ? 'Approve' : 'Unapprove'}
                </Button>
              </p>
            </div>
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900 rounded">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Deactivated
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 break-words flex gap-3">
                <Badge
                  dataCy="merchant-kyb-deactivated-badge"
                  label={isDeactivated ? 'Yes' : 'No'}
                  colorScheme={
                    statusColors[!isDeactivated ? 'successful' : 'failed']
                  }
                  className="px-4 !rounded-3xl"
                />
                <PermissionsProvider
                  permission={AfriexAdminPermission.BusinessFlagUpdate}
                >
                  <Button
                    data-cy="merchant-kyb-deactivated-toggle"
                    disabled={businessLoadingFlags['isDeactivated']}
                    variant="unStyled"
                    onClick={() =>
                      onFlagUpdate({
                        isDeactivated: !isDeactivated,
                      })
                    }
                    className="hover:underline text-cyan-700"
                  >
                    {isDeactivated ? 'Activate' : 'Deactivate'}
                  </Button>
                </PermissionsProvider>
              </p>
            </div>
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900 rounded">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Charge Fee via Wallet
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 break-words flex gap-3">
                <Badge
                  dataCy="merchant-kyb-fee-wallet-badge"
                  label={isApprovedForFeeWallet ? 'Yes' : 'No'}
                  colorScheme={
                    statusColors[
                      isApprovedForFeeWallet ? 'successful' : 'failed'
                    ]
                  }
                  className="px-4 !rounded-3xl"
                />
                <Button
                  data-cy="merchant-kyb-fee-wallet-toggle"
                  disabled={businessLoadingFlags['isApprovedForFeeWallet']}
                  variant="unStyled"
                  onClick={() =>
                    onFlagUpdate({
                      isApprovedForFeeWallet: !isApprovedForFeeWallet,
                    })
                  }
                  className="hover:underline text-cyan-700"
                >
                  {isApprovedForFeeWallet ? 'Disable' : 'Enable'}
                </Button>
              </p>
            </div>
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900 rounded">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Approved for Post-Funding
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 break-words flex gap-3">
                <Badge
                  dataCy="merchant-kyb-post-funding-approval-badge"
                  label={isApprovedForPostFunding ? 'Yes' : 'No'}
                  colorScheme={
                    statusColors[
                      isApprovedForPostFunding ? 'successful' : 'failed'
                    ]
                  }
                  className="px-4 !rounded-3xl"
                />
                <Button
                  data-cy="merchant-kyb-post-funding-approval-toggle"
                  disabled={businessLoadingFlags['isApprovedForPostFunding']}
                  variant="unStyled"
                  onClick={() =>
                    onFlagUpdate({
                      isApprovedForPostFunding: !isApprovedForPostFunding,
                    })
                  }
                  className="hover:underline text-cyan-700"
                >
                  {!isApprovedForPostFunding ? 'Approve' : 'Unapprove'}
                </Button>
              </p>
            </div>
            {isApprovedForPostFunding && (
              <PostFundingConfigForm
                business={business}
                onSubmit={onPostFundingConfigUpdate}
                isSubmitting={isPostFundingConfigSubmitting}
              />
            )}
          </div>

          {extraRow || ''}
        </div>
      </div>

      <KYBModal
        isOpen={openUploadModal}
        onClose={() => setOpenUploadModal(false)}
        type={modalType}
        onSubmit={onSubmit}
        isKycSubmitting={isKycSubmitting}
        business={business}
      />

      <RequestAdditionalDocumentsModal
        isOpen={openRequestDocumentsModal}
        onClose={() => setOpenRequestDocumentsModal(false)}
        options={requestableBusinessKYBDocuments}
        onSubmit={onRequestAdditionalDocuments}
        description="Select the documents you require from this business from the list below"
      />
    </div>
  );
};

export default Summary;
